/**
 * Stable tile ordering.
 *
 * Churn is expensive: every reorder that moves a publisher across the page
 * boundary costs a subscribe/unsubscribe round trip and a keyframe. So the
 * order is deterministic (join time) and speaker promotion is applied ONLY
 * when the room is bigger than one page — below that nothing ever moves.
 */
import type { MediaParticipant } from './contract';

export interface OrderContext {
  readonly activeSpeakerIds: readonly string[];
  readonly pinnedId: string | null;
  readonly pageSize: number;
  /** Local self-view is rendered last in the grid, like every other product. */
  readonly localId: string;
}

const priority = (p: MediaParticipant, ctx: OrderContext, promote: boolean): number => {
  if (p.spotlighted) return 0;
  if (p.id === ctx.pinnedId) return 1;
  if (p.sharing) return 2;
  if (promote) {
    const speakerIndex = ctx.activeSpeakerIds.indexOf(p.id);
    if (speakerIndex >= 0) return 3 + speakerIndex / 100;
    if (p.handRaisedAt !== null) return 4;
    if (p.cameraOn) return 5;
    return 6;
  }
  return 5;
};

/** Deterministic ordering; never mutates the input array. */
export function orderTiles(
  participants: readonly MediaParticipant[],
  ctx: OrderContext,
): readonly MediaParticipant[] {
  const promote = participants.length > ctx.pageSize;
  return [...participants].sort((a, b) => {
    const byPriority = priority(a, ctx, promote) - priority(b, ctx, promote);
    if (byPriority !== 0) return byPriority;
    // Self last within its band, then oldest join first, then id for total order.
    if (a.isLocal !== b.isLocal) return a.isLocal ? 1 : -1;
    if (a.joinedAt !== b.joinedAt) return a.joinedAt - b.joinedAt;
    return a.id < b.id ? -1 : 1;
  });
}

/** Column count for a tile count — 3 columns is the reference layout. */
export function columnsFor(tileCount: number): number {
  if (tileCount <= 1) return 1;
  if (tileCount <= 4) return 2;
  return 3;
}
